import { useState, useEffect } from 'react';
import { Download, Database, Shield, AlertTriangle, CheckCircle, Loader, Server, Users, FileText, Clock, Lock, HardDrive } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuditLog } from '../hooks/useAuditLog';
import { format } from 'date-fns';

export function DatabaseBackup() {
  const [isGenerating, setIsGenerating] = useState(false);
  const [isFetchingCounts, setIsFetchingCounts] = useState(false);
  const [hasLoadedCounts, setHasLoadedCounts] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [backupStats, setBackupStats] = useState<Record<string, number>>({});
  const [progress, setProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const { logAction } = useAuditLog();

  // Complete list of tables in the database
  const databaseTables = [
    { name: 'devices', icon: HardDrive, description: 'Device records, serial numbers, owners, lock status' },
    { name: 'device_history', icon: Clock, description: 'Historical changes and audit trail for devices' },
    { name: 'admins', icon: Users, description: 'Administrator accounts and permissions' },
    { name: 'audit_log', icon: FileText, description: 'Complete system audit logs and user actions' },
    { name: 'finance_users', icon: Users, description: 'Finance team user accounts and access levels' },
    { name: 'lock_requests', icon: Lock, description: 'Device lock/unlock requests from finance team' },
    { name: 'lock_app_runs', icon: Server, description: 'Lock application execution history and timestamps' }
  ];

  // Log page access (intentionally no-op)
  useEffect(() => {
    // intentionally empty – we only log when running a backup
  }, []); // no dependency needed

  // Helper function to progress with delays
  const updateProgress = async (value: number, step: string, delay: number = 2000) => {
    setProgress(value);
    setCurrentStep(step);
    await new Promise(resolve => setTimeout(resolve, delay));
  };

  const generateBackup = async () => {
    setIsGenerating(true);
    setError(null);
    setSuccess(false);
    setProgress(0);
    setCurrentStep('Initializing backup process...');

    try {
      // Step 1: Initialize
      await updateProgress(5, 'Initializing backup process...', 3000);

      // Log backup initiation
      await logAction('run_db_backup', 'admin_management', {
        resourceType: 'database',
        resourceId: selectedTable || 'full_backup',
        details: {
          action: 'backup_initiated',
          selectedTable: selectedTable || 'all'
        }
      });

      await updateProgress(10, 'Connecting to database...', 2500);

      // Get all tables data - using the complete table list or just the selected table
      const tableNames = selectedTable
        ? [selectedTable]
        : databaseTables.map(table => table.name);

      const backupData: Record<string, unknown[]> = {};
      const tableStats: Record<string, number> = {};

      await updateProgress(15, 'Preparing table list...', 2000);

      // Process each table with progress updates
      for (let i = 0; i < tableNames.length; i++) {
        const tableName = tableNames[i];
        const progressValue = 15 + ((i + 1) / tableNames.length) * 60; // 15% to 75%

        await updateProgress(progressValue, `Backing up ${tableName} table...`, 2500);

        try {
          let query = supabase.from(tableName).select('*');

          if ([
            'devices',
            'device_history',
            'admins',
            'audit_log',
            'finance_users',
            'lock_requests'
          ].includes(tableName)) {
            query = query.order('created_at', { ascending: false });
          } else if (tableName === 'lock_app_runs') {
            query = query.order('timestamp', { ascending: false });
          }

          const { data, error: tableError } = await query;

          if (tableError) {
            console.warn(`Warning: Could not back up table ${tableName}:`, tableError);
            backupData[tableName] = [];
            tableStats[tableName] = 0;
          } else {
            backupData[tableName] = data || [];
            tableStats[tableName] = (data?.length as number) || 0;
          }
        } catch (err) {
          console.warn(`Warning: Could not back up table ${tableName}:`, err);
          backupData[tableName] = [];
          tableStats[tableName] = 0;
        }
      } // <-- closes for-loop

      // Create backup metadata
      const backupMetadata = {
        generated_at: new Date().toISOString(),
        generated_by: 'PayinthiDMS Admin',
        version: '2.0',
        tables: Object.keys(backupData),
        table_statistics: tableStats,
        total_records: Object.values(backupData).reduce((sum, records) => sum + (records?.length || 0), 0),
        database_schema_version: 'v1.5'
      };

      await updateProgress(85, 'Creating backup file...', 2000);

      // Create the backup object
      const backup = {
        metadata: backupMetadata,
        data: backupData
      };

      await updateProgress(90, 'Converting to JSON format...', 2500);

      // Convert to JSON
      const backupJson = JSON.stringify(backup, null, 2);

      await updateProgress(95, 'Preparing download...', 2000);

      // Create and download the file
      const blob = new Blob([backupJson], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = selectedTable
        ? `table_backup_${selectedTable}_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.json`
        : `database_backup_${format(new Date(), 'yyyy-MM-dd_HH-mm-ss')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      await updateProgress(100, `${selectedTable ? `${selectedTable} table` : 'Database'} backup completed successfully!`, 3000);

      // Log successful backup
      await logAction('run_db_backup', 'admin_management', {
        resourceType: 'database',
        resourceId: selectedTable || 'full_backup',
        details: {
          action: 'backup_completed',
          total_records: backupMetadata.total_records,
          tables: backupMetadata.tables,
          selectedTable: selectedTable || 'all'
        },
        success: true
      });

      setSuccess(true);
      setBackupStats(tableStats); // ensure stats shown in success panel match this run

      setTimeout(() => {
        setSuccess(false);
        setProgress(0);
        setCurrentStep('');
        setSelectedTable(null); // Clear selected table after successful backup
      }, 5000);

    } catch (err: unknown) {
      console.error('Backup generation failed:', err);
      setError('Failed to generate database backup. Please try again.');
      setProgress(0);
      setCurrentStep('');

      // Log failed backup
      await logAction('run_db_backup', 'admin_management', {
        resourceType: 'database',
        resourceId: selectedTable || 'full_backup',
        details: { action: 'backup_failed' },
        success: false,
        errorMessage: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchTableCounts = async () => {
    setIsFetchingCounts(true);
    const tableStats: Record<string, number> = {};

    try {
      for (const table of databaseTables) {
        try {
          const { count, error: countError } = await supabase
            .from(table.name)
            .select('*', { count: 'exact', head: true });

          if (countError) {
            console.warn(`Warning: Could not count table ${table.name}:`, countError);
            tableStats[table.name] = 0;
          } else {
            tableStats[table.name] = count || 0;
          }
        } catch (err) {
          console.warn(`Warning: Could not count table ${table.name}:`, err);
          tableStats[table.name] = 0;
        }
      }

      setBackupStats(tableStats);
      setHasLoadedCounts(true);

      // Log the count fetch action
      await logAction('fetch_table_counts', 'admin_management', {
        resourceType: 'database',
        resourceId: 'table_counts',
        details: {
          action: 'counts_fetched',
          total_records: Object.values(tableStats).reduce((sum, c) => sum + c, 0),
          tables: Object.keys(tableStats)
        },
        success: true
      });

    } catch (err: unknown) {
      console.error('Failed to fetch table counts:', err);
      // Log failed count fetch
      await logAction('fetch_table_counts', 'admin_management', {
        resourceType: 'database',
        resourceId: 'table_counts',
        details: { action: 'counts_failed' },
        success: false,
        errorMessage: err instanceof Error ? err.message : 'Unknown error'
      });
    } finally {
      setIsFetchingCounts(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-4 px-2">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white dark:bg-gray-800 border-2 border-gray-400 dark:border-gray-600">
          {/* Header */}
          <div className="bg-gray-300 dark:bg-gray-700 px-4 py-3 border-b-2 border-gray-400 dark:border-gray-600">
            <div className="flex items-center space-x-2">
              <Database className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">DATABASE BACKUP UTILITY v2.1.3</h1>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Administrative Backend Tool - PostgreSQL Data Export Module
            </p>
            <div className="text-xs text-gray-500 dark:text-gray-500 mt-2">
              System Status: OPERATIONAL | Server: NANO AWS | Region: AP-SOUTHEAST-2 | Protocol: TCP/IP
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            {/* Database Tables Overview */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
              <div className="border-2 border-gray-400 dark:border-gray-600 bg-gray-50 dark:bg-gray-800">
                <div className="bg-gray-200 dark:bg-gray-700 px-3 py-2 border-b-2 border-gray-400 dark:border-gray-600">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <HardDrive className="w-4 h-4 text-gray-700 dark:text-gray-300" />
                      <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase">
                        DATABASE SCHEMA ({databaseTables.length} TABLES)
                      </h3>
                    </div>
                    {selectedTable && (
                      <div className="text-xs text-blue-700 dark:text-blue-400 font-mono">
                        TABLE SELECTED: {selectedTable}
                      </div>
                    )}
                  </div>
                </div>
                <div className="p-3 max-h-80 overflow-y-auto">
                  <div className="flex items-center justify-between mb-2 pb-1 border-b border-gray-400">
                    <div className="text-xs text-gray-600 dark:text-gray-400 font-mono">
                      TABLE RECORD COUNTS {selectedTable && <span className="text-blue-600 dark:text-blue-400">(CLICK TO SELECT/DESELECT)</span>}
                    </div>
                    <button
                      onClick={fetchTableCounts}
                      disabled={isFetchingCounts || isGenerating}
                      className="px-2 py-1 bg-gray-600 text-white border border-gray-800 hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-xs font-mono uppercase"
                    >
                      {isFetchingCounts ? (
                        <div className="flex items-center space-x-1">
                          <Loader className="w-3 h-3 animate-spin" />
                          <span>SCAN</span>
                        </div>
                      ) : (
                        hasLoadedCounts ? 'REFRESH' : 'SCAN'
                      )}
                    </button>
                  </div>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="border-b border-gray-400">
                        <th className="text-left py-1 px-2 font-bold">TABLE</th>
                        <th className="text-left py-1 px-2 font-bold">TYPE</th>
                        <th className="text-left py-1 px-2 font-bold">RECORDS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {databaseTables.map((table, index) => (
                        <tr 
                          key={table.name} 
                          className={`${index % 2 === 0 ? 'bg-gray-100 dark:bg-gray-700' : ''} ${selectedTable === table.name ? 'bg-blue-100 dark:bg-blue-900/40 font-bold' : ''} cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20`}
                          onClick={() => setSelectedTable(selectedTable === table.name ? null : table.name)}
                        >
                          <td className="py-1 px-2 font-mono">
                            {table.name}
                            {selectedTable === table.name && (
                              <span className="ml-2 text-blue-600 dark:text-blue-400">▶</span>
                            )}
                          </td>
                          <td className="py-1 px-2 text-gray-600 dark:text-gray-400">DATA</td>
                          <td className="py-1 px-2">
                            {backupStats[table.name] !== undefined ? backupStats[table.name] : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="space-y-4">
                {/* System Information */}
                <div className="border-2 border-blue-400 bg-blue-50 dark:bg-blue-900/20 p-3">
                  <div className="flex items-center space-x-2 mb-2">
                    <Server className="w-4 h-4 text-blue-600" />
                    <h3 className="text-sm font-bold text-blue-900 dark:text-blue-100 uppercase">
                      SYSTEM INFORMATION
                    </h3>
                  </div>
                  <div className="text-xs text-blue-800 dark:text-blue-200 font-mono leading-tight space-y-1">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p>DATABASE: PostgreSQL</p>
                        <p>HOST: NANO AWS | AP-SOUTHEAST-2</p>
                        <p>PROTOCOL: HTTPS/REST</p>
                        <p>FORMAT: JSON Export</p>
                      </div>
                      <div>
                        <p>TABLES: {databaseTables.length}</p>
                        <p>ENCODING: UTF-8</p>
                        <p>COMPRESSION: None</p>
                        <p>METADATA: Included</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Status Messages */}
            {error && (
              <div className="border-2 border-red-600 bg-red-100 dark:bg-red-900/30 p-3 mb-4">
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <h4 className="text-sm font-bold text-red-900 dark:text-red-100 uppercase">
                    ERROR: BACKUP OPERATION FAILED
                  </h4>
                </div>
                <p className="text-xs text-red-800 dark:text-red-200 mt-1 font-mono">{error}</p>
              </div>
            )}

            {success && (
              <div className="border-2 border-green-600 bg-green-100 dark:bg-green-900/30 p-3 mb-4">
                <div className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <h4 className="text-sm font-bold text-green-900 dark:text-green-100 uppercase">
                    SUCCESS: BACKUP OPERATION COMPLETED
                  </h4>
                </div>
                <p className="text-xs text-green-800 dark:text-green-200 mt-1">
                  Database export generated and downloaded to local filesystem.
                </p>
                {Object.keys(backupStats).length > 0 && (
                  <div className="mt-2 p-2 bg-white dark:bg-gray-800 border border-gray-400">
                    <p className="text-xs font-bold text-gray-900 dark:text-white mb-1 uppercase">
                      Export Statistics:
                    </p>
                    <div className="text-xs font-mono">
                      {Object.entries(backupStats).map(([table, count]) => (
                        <div key={table} className="flex justify-between py-0.5">
                          <span>{table}:</span>
                          <span>{count} records</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Action Section */}
            <div className="border-2 border-gray-400 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 p-4 mb-4">
              <div className="bg-gray-200 dark:bg-gray-700 px-3 py-2 border-2 border-gray-400 dark:border-gray-600 mb-3">
                <h3 className="text-sm font-bold text-gray-900 dark:text-white uppercase">
                  BACKUP EXECUTION CONTROL PANEL
                </h3>
              </div>
              
              <div className="text-center">
                <p className="text-xs text-gray-600 dark:text-gray-400 mb-4 font-mono">
                  [READY] Database connection established. {databaseTables.length} tables detected. 
                  {selectedTable ? 
                    <span className="text-blue-600 dark:text-blue-400"> TABLE SELECTION: [ {selectedTable} ]</span> : 
                    ' JSON export module loaded.'}
                </p>

                <button
                  onClick={generateBackup}
                  disabled={isGenerating}
                  className="px-6 py-3 bg-blue-600 text-white border-2 border-blue-800 hover:bg-blue-700 disabled:bg-gray-400 disabled:border-gray-500 disabled:cursor-not-allowed text-sm font-bold uppercase"
                >
                  {isGenerating ? (
                    <div className="flex items-center justify-center space-x-2">
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>EXECUTING BACKUP PROCEDURE...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-2">
                      <Download className="w-4 h-4" />
                      <span>{selectedTable ? `EXPORT TABLE: ${selectedTable}` : 'EXECUTE FULL DATABASE BACKUP'}</span>
                    </div>
                  )}
                </button>
                
                {selectedTable && !isGenerating && (
                  <button
                    onClick={() => setSelectedTable(null)}
                    className="ml-2 px-3 py-2 bg-gray-500 text-white border-2 border-gray-700 hover:bg-gray-600 text-xs font-mono uppercase"
                  >
                    CLEAR SELECTION
                  </button>
                )}

                {/* Progress Bar */}
                {isGenerating && (
                  <div className="mt-4 w-full max-w-md mx-auto">
                    <div className="bg-gray-300 dark:bg-gray-600 border-2 border-gray-500 h-6">
                      <div 
                        className="h-full bg-blue-500 border-r-2 border-blue-700"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="mt-2 text-center">
                      <p className="text-xs font-mono text-gray-700 dark:text-gray-300">
                        {currentStep}
                      </p>
                      <p className="text-xs font-mono text-gray-500 dark:text-gray-400">
                        PROGRESS: {progress}% | STATUS: {progress < 100 ? 'PROCESSING' : 'COMPLETED'}
                      </p>
                    </div>
                  </div>
                )}
                
                {!isGenerating && (
                  <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 font-mono space-y-1">
                    <div>EST. PROCESSING TIME: ~45 SECONDS</div>
                    <div>EST. FILE SIZE: 1-10MB</div>
                    <div>OUTPUT FORMAT: application/json</div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="border-2 border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Shield className="w-4 h-4 text-yellow-600" />
                  <div className="text-xs">
                    <p className="font-bold text-yellow-900 dark:text-yellow-100 uppercase">
                      RESTRICTED ACCESS - ADMIN ONLY
                    </p>
                    <p className="text-yellow-700 dark:text-yellow-300 font-mono">
                      All access logged.
                    </p>
                  </div>
                </div>
                <div className="text-xs text-yellow-600 dark:text-yellow-400 font-mono bg-white dark:bg-gray-800 px-2 py-1 border border-gray-400">
                  Route: /dbbackup
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
